/**
 * The 400 a link request gets when it leaves out a screening confirmation that is not on file (the form asked
 * before the requirements changed): the modal refetches them and asks again.
 */
export const applyLinkConfirmationRequiredErrorCode = 'APPLY_LINK_CONFIRMATION_REQUIRED';

/**
 * What stops a link from sending on its own, judged from what is on file:
 * - `attestations`: nobody has confirmed TransUnion's attestations for the unit;
 * - `permissible-purpose-certification`: the sender's FCRA certification is missing, older than 90 days or on an
 *   earlier wording (RentPrep only);
 * - `rent-prep-terms`: the sender never accepted RentPrep's terms (RentPrep only);
 * - `sender-access`: the seat it sends as left the team or can no longer manage the unit; confirming does not fix
 *   it, a seat that can manage the unit creates its own link.
 */
export type ApplyLinkBlockerItem =
  | 'attestations'
  | 'permissible-purpose-certification'
  | 'rent-prep-terms'
  | 'sender-access';

export interface ApplyLinkBlockerDto {
  item: ApplyLinkBlockerItem;
  /** Why the item is asked again; absent when it was never on file. */
  reason?: 'never-confirmed' | 'requirements-changed' | 'certification-expired' | 'wording-changed';
}

/**
 * One seat's active apply link for a unit. A link always sends as the seat that created it; any seat that can view
 * the unit sees the team's links and may copy them, only the creator renews or resets their own.
 */
export interface ApplyLinkDto {
  id: string;
  /** The renter app's apply page; the owner app never builds it. */
  url: string;
  /** The link sends as the caller. */
  isMine: boolean;
  sender: { investorId: string; name: string };
  createdAt: string;
  /** `needs-confirmation`: paused, an application through it waits for the sender to send it. */
  status: 'active' | 'needs-confirmation';
  blockers: ApplyLinkBlockerDto[];
  /** When the sender's FCRA certification stops covering sends and the link pauses; null while TransUnion screens everything. */
  expiresAt: string | null;
}
